/**
 * Regras de data do calendário próprio. Tudo trabalha com texto ISO "AAAA-MM-DD" (o mesmo formato que a API e o
 * <input type="date"> sempre usaram) e faz a conta em UTC, para o fuso horário nunca deslocar o dia.
 */

export const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'] as const;
export const DIAS_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'] as const;
export const DIAS_SEMANA_LONGO = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'] as const;

export type Dia = { a: number; m: number; d: number };

const pad = (n: number, t = 2) => String(n).padStart(t, '0');

export const diasNoMes = (a: number, m: number) => new Date(Date.UTC(a, m, 0)).getUTCDate(); // m: 1..12

/** "2026-02-30" não existe: devolve null em vez de "corrigir" para março. */
export function lerIso(s: string | null | undefined): Dia | null {
  const r = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s ?? '');
  if (!r) return null;
  const a = Number(r[1]);
  const m = Number(r[2]);
  const d = Number(r[3]);
  if (a < 1900 || a > 2200 || m < 1 || m > 12 || d < 1 || d > diasNoMes(a, m)) return null;
  return { a, m, d };
}

export const paraIso = ({ a, m, d }: Dia) => `${pad(a, 4)}-${pad(m)}-${pad(d)}`;

export function hojeIso(agora = new Date()): string {
  // data LOCAL de quem está usando (não a do UTC, que na noite do Brasil já é "amanhã")
  return `${pad(agora.getFullYear(), 4)}-${pad(agora.getMonth() + 1)}-${pad(agora.getDate())}`;
}

/** "2026-11-04" → "04/11/2026" ("" se vazio ou inválido). */
export function formatarBr(iso: string | null | undefined): string {
  const x = lerIso(iso);
  return x ? `${pad(x.d)}/${pad(x.m)}/${pad(x.a, 4)}` : '';
}

/** Máscara de digitação: só números, com as barras entrando sozinhas ("04112026" → "04/11/2026"). */
export function mascaraBr(texto: string): string {
  const n = texto.replace(/\D/g, '').slice(0, 8);
  if (n.length <= 2) return n;
  if (n.length <= 4) return `${n.slice(0, 2)}/${n.slice(2)}`;
  return `${n.slice(0, 2)}/${n.slice(2, 4)}/${n.slice(4)}`;
}

/** "04/11/2026" → "2026-11-04"; null se incompleto ou se a data não existe. */
export function lerBr(texto: string): string | null {
  const r = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(texto.trim());
  if (!r) return null;
  const dia: Dia = { a: Number(r[3]), m: Number(r[2]), d: Number(r[1]) };
  const iso = paraIso(dia);
  return lerIso(iso) ? iso : null;
}

export function somarDias(iso: string, n: number): string {
  const x = lerIso(iso);
  if (!x) return iso;
  const t = new Date(Date.UTC(x.a, x.m - 1, x.d + n));
  return paraIso({ a: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() });
}

/** Soma meses mantendo o dia, e "encolhe" para o último dia quando o mês não tem tanto (31/01 + 1 mês = 28/02). */
export function somarMeses(iso: string, n: number): string {
  const x = lerIso(iso);
  if (!x) return iso;
  const total = x.a * 12 + (x.m - 1) + n;
  const a = Math.floor(total / 12);
  const m = (total % 12) + 1;
  return paraIso({ a, m, d: Math.min(x.d, diasNoMes(a, m)) });
}

export type CelulaDia = { iso: string; dia: number; doMes: boolean };

/** Grade do mês: 6 semanas (42 células) começando no domingo, com os dias vizinhos para completar. */
export function gradeDoMes(ano: number, mes: number): CelulaDia[] {
  const primeiro = new Date(Date.UTC(ano, mes - 1, 1));
  const inicio = new Date(Date.UTC(ano, mes - 1, 1 - primeiro.getUTCDay()));
  return Array.from({ length: 42 }, (_, i) => {
    const t = new Date(Date.UTC(inicio.getUTCFullYear(), inicio.getUTCMonth(), inicio.getUTCDate() + i));
    const a = t.getUTCFullYear();
    const m = t.getUTCMonth() + 1;
    const d = t.getUTCDate();
    return { iso: paraIso({ a, m, d }), dia: d, doMes: a === ano && m === mes };
  });
}

export function foraDoLimite(iso: string, min?: string, max?: string): boolean {
  return (!!min && iso < min) || (!!max && iso > max);
}

/** Nome por extenso para leitor de tela: "quarta-feira, 4 de novembro de 2026". */
export function porExtenso(iso: string): string {
  const x = lerIso(iso);
  if (!x) return iso;
  const dow = new Date(Date.UTC(x.a, x.m - 1, x.d)).getUTCDay();
  return `${DIAS_SEMANA_LONGO[dow]}, ${x.d} de ${MESES[x.m - 1]} de ${x.a}`;
}
